import React, { useCallback, useEffect, useState } from "react";
import { format, subMonths } from "date-fns";
import { Trans } from "translations";
import DateInput from "../../../DateInput/DateInput";
import {
  getEarliestAllowedStartDate,
  himHer,
  validStartDate,
} from "../../../../utils/insurance";
import { SpeciesEnum } from "../../../../../__generated__/globalTypes";
import {
  MAX_AGE_CAT,
  MAX_AGE_DOG,
} from "../../../../components-shared/insuranceData";
import { Typography } from "../../../../ui-kit/Typography";
import { EmailT, useWebTranslation } from "../../../../translations";
import ChatMessage, {
  ChatContainer,
} from "../../../../ui-kit/ChatMessage/ChatMessage";
import { FlexRow } from "../../../../ui-kit/Layout/Flex";
import {
  getDefaultLocale,
  getDateFormat,
  getCampaigns,
} from "../../../../utils/site";
import { getYoungAnimalDiscount } from "../../../../utils/discount";
import { speciesToEmoji } from "../../../../utils/insurance";
import Colors from "../../../../ui-kit/colors";
import Gutter from "../../../../ui-kit/Gutter";
import { getDateFnsLocaleFromLocale } from "../../../../utils/utils";
import { StepProps } from "../types";
import { useSignupDispatch } from "components/Signup/store/hooks";
import {
  updateAnimalById,
  updateInsuranceById,
} from "components/Signup/store/slices/animals";
import FlowButtons from "components/Signup/components/FlowButtons/FlowButtons";
import { useCurrentInsuranceByAnimalId } from "components/Signup/store/selectors/insurance";
import { useCurrentAnimal } from "components/Signup/store/selectors/animals";

const AnimalBirthday = ({
  previous,
  next,
  isValid,
  transitioning,
}: StepProps) => {
  const { t } = useWebTranslation();
  const [hasPostponedStartDate, setHasPostponedStartDate] =
    useState<boolean>(false);
  const [dateValid, setDateValid] = useState(true);
  const [dateValidMin, setDateValidMin] = useState(true);
  const [dateValidMax, setDateValidMax] = useState(true);
  const dateFormat = getDateFormat();

  const { birthday, species, sex, name, id } = useCurrentAnimal();
  const {
    startDate,
    discountCode,
    id: insuranceId,
  } = useCurrentInsuranceByAnimalId(id);
  const dispatch = useSignupDispatch();

  const youngAnimalDiscount = getYoungAnimalDiscount({
    birthday,
    species,
  });

  const min = subMonths(
    new Date(),
    species === SpeciesEnum.dog ? MAX_AGE_DOG : MAX_AGE_CAT
  );
  const max = new Date();

  useEffect(() => {
    if (isValid) {
      const startDate = validStartDate({
        birthday: birthday!,
      });

      setHasPostponedStartDate(
        new Date(startDate) > getEarliestAllowedStartDate()
      );

      dispatch(
        updateInsuranceById(
          { id: insuranceId, update: { startDate } },
          { refetch: false }
        )
      );
    } else {
      setHasPostponedStartDate(false);
    }
  }, [birthday]);

  const hasError = !dateValid || !dateValidMin || !dateValidMax;

  return (
    <ChatContainer leaving={transitioning}>
      <ChatMessage index={1}>
        <Typography>
          {t(`signup.animal_birthday.text_${sex}`, {
            name: name ?? "",
          })}
        </Typography>
        <Gutter size={1.5} />
        <Typography color="grey500">
          {t("signup.animal_birthday.description")}
        </Typography>
      </ChatMessage>
      <Gutter size={2} />
      <ChatMessage fromMe small index={2}>
        <DateInput
          noValidate
          border={false}
          value={birthday ?? ""}
          onChange={({ date, validation }) => {
            if (validation.isValid) {
              setDateValid(true);
            }
            setDateValidMax(validation.isValidMax);
            setDateValidMin(validation.isValidMin);

            dispatch(updateAnimalById({ id, update: { birthday: date } }));
          }}
          onBlur={({ validation }) => {
            setDateValid(validation.isValid);
          }}
          min={min}
          max={max}
          ariaLabel={t("global.birthdate")}
          ariaDescribedBy={hasError ? "error-message" : undefined}
          id="animal-birthday-input"
        />
      </ChatMessage>
      {youngAnimalDiscount && (
        <>
          <Gutter size={2} />
          <ChatMessage index={3}>
            <Typography variant="h4">{`😍 ${speciesToEmoji(
              species
            )}`}</Typography>
            <Gutter size={0.5} />
            <Typography>
              {t(
                `signup.animal_birthday.${
                  species === SpeciesEnum.dog ? "puppy" : "kitten"
                }_discount.title`
              )}
            </Typography>
            <Gutter size={1} />
            <Typography color="grey500">
              {t(
                `signup.animal_birthday.${
                  species === SpeciesEnum.dog ? "puppy" : "kitten"
                }_discount.msg`,
                {
                  name: name ?? "",
                }
              )}
            </Typography>
          </ChatMessage>
        </>
      )}
      {hasPostponedStartDate && startDate && (
        <>
          <Gutter size={2} />
          <ChatMessage>
            <Typography color="grey500">
              {t("signup.animal_birthday.postponed", {
                date: format(new Date(startDate), "do MMMM", {
                  locale: getDateFnsLocaleFromLocale(getDefaultLocale()),
                }),
                him: himHer(sex ?? ""),
                name: name ?? "",
              })}
            </Typography>
          </ChatMessage>
        </>
      )}
      {!dateValid && (
        <div id="error-message">
          <Gutter size={1} />
          <Typography color={"error"} testId="date-error">
            <Trans
              i18nKey="date_input.error"
              tOptions={{
                date: format(min, dateFormat),
              }}
              components={{
                email: <EmailT color={Colors.error} />,
              }}
            />
          </Typography>
          <Gutter size={1} />
        </div>
      )}
      {!dateValidMin && (
        <div id="error-message">
          <Gutter size={1} />
          <Typography color={"error"}>
            <Trans
              data-testid="date-error-min"
              i18nKey="date_input.error_min"
              tOptions={{
                date: format(min, dateFormat),
              }}
              components={{
                email: <EmailT color={Colors.error} />,
              }}
            />
          </Typography>
          <Gutter size={1} />
        </div>
      )}
      {!dateValidMax && (
        <div id="error-message">
          <FlexRow justifyContent="flex-end">
            <Gutter size={1} />
            <Typography color={"error"}>
              <Trans
                i18nKey="signup.animal_birthday.max_error"
                tOptions={{
                  date: format(max, dateFormat),
                }}
                components={{
                  email: <EmailT color={Colors.error} />,
                }}
              />
            </Typography>
            <Gutter size={1} />
          </FlexRow>
        </div>
      )}
      <FlowButtons
        inProgress={transitioning}
        backButton={false}
        next={next}
        disabled={!isValid}
        previous={previous}
      />
    </ChatContainer>
  );
};

export default AnimalBirthday;
